import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { BuildIcon, EditIcon, ExpandMoreIcon, FastForwardIcon, HealingIcon, ReceiptLongIcon, ScienceIcon, VisibilityIcon } from "../../icons";
import { useRootStore } from "../../store/contexts/RootStoreContext";
import { AutomationStore, RESOLUTION_GUIDE } from "./AutomationStore";
import { StatusChip } from "../common/StatusChip";
import { RetryIndicator } from "../common/RetryIndicator";
import { OverridesEditor } from "./OverridesEditor";

const fmtSince = (iso?: string | null): string => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "recién";
  if (mins < 60) return `hace ${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
};

const errText = (error: any): string =>
  error ? String(error?.message?.es ?? error?.message ?? error ?? "") : "";

/**
 * Caja de intervención AUTOCONTENIDA de una edición en dead-end: diagnóstico
 * (error/intentos/payload), guía de causas con acción al lado, editor de
 * correcciones de trámite, y las acciones de resolución — todo in-situ.
 */
export const InterventionCard = observer(
  ({ store, edition }: { store: AutomationStore; edition: any }) => {
    const { router } = useRootStore();
    const [showOverrides, setShowOverrides] = useState(false);

    const dnda = edition?.dndaState?.id as string | undefined;
    const sadaic = edition?.sadaicState?.id as string | undefined;
    const failureState = (dnda && RESOLUTION_GUIDE[dnda] ? dnda : undefined) ?? (sadaic && RESOLUTION_GUIDE[sadaic] ? sadaic : undefined);
    const guide = failureState ? RESOLUTION_GUIDE[failureState] : [];
    const deadLetter = store.deadLetterForEdition(edition.id);
    const botRun = store.botRunForEdition(edition.id);
    const recoveries = store.recoveryOptionsFor(edition);
    const name = edition?.data_?.name ?? edition.displayId ?? edition.id;
    const dndaError = edition?.dndaLastOperationalError as string | undefined;
    const sadaicError = edition?.sadaicLastOperationalError as string | undefined;

    const guideAction = (action: string) => {
      switch (action) {
        case "tenant":
          return (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() =>
                // Navegar al Tenant del RECURSO (la edición intervenida puede ser
                // de otro tenant; Tenant es shared → el SysAdmin puede editarlo).
                router.goTo(edition.tenant_id_ ? `/tenant/update/${edition.tenant_id_}` : "/tenant/list")
              }
            >
              Editar Tenant{edition.tenant_id_ ? ` (${edition.tenant_id_})` : ""}
            </Button>
          );
        case "payments":
          return (
            <Button size="small" variant="outlined" startIcon={<ReceiptLongIcon />} onClick={() => router.goTo("/paymentReceipt/list")}>
              Ir a Pagos
            </Button>
          );
        case "overrides":
          return (
            <Button size="small" variant="outlined" startIcon={<HealingIcon />} onClick={() => setShowOverrides(true)}>
              Corregir datos del trámite
            </Button>
          );
        default:
          return null; // "rerun": la acción es el CTA primario de abajo.
      }
    };

    const confirmAndResolve = (mutation: string, label: string) => {
      const activeOverrides = (store.overridesByEdition[edition.id] ?? []).filter((o) => o.active);
      const summary = [
        `${label} — "${name}"`,
        "",
        "El reintento rearma los datos del trámite desde sus fuentes:",
        "• Credenciales y configuración → del Tenant (frescas)",
        activeOverrides.length > 0
          ? `• ${activeOverrides.length} corrección(es) de trámite activa(s) se aplican sobre el contrato`
          : "• Sin correcciones de trámite activas (el contrato se usa tal cual)",
        "",
        "¿Continuar?",
      ].join("\n");
      if (window.confirm(summary)) {
        void store.resolveEdition(edition, mutation);
      }
    };

    return (
      <Card variant="outlined" sx={{ borderColor: "error.main", borderWidth: 2 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">{name}</Typography>
            {dnda && (
              <Typography variant="body2" sx={{ fontFamily: "monospace", color: RESOLUTION_GUIDE[dnda] ? "error.main" : "text.primary", fontWeight: RESOLUTION_GUIDE[dnda] ? 700 : 400 }}>
                DNDA: {dnda}
              </Typography>
            )}
            {sadaic && (
              <Typography variant="body2" sx={{ fontFamily: "monospace", color: RESOLUTION_GUIDE[sadaic] ? "error.main" : "text.primary", fontWeight: RESOLUTION_GUIDE[sadaic] ? 700 : 400 }}>
                SADAIC: {sadaic}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {fmtSince(deadLetter?.enqueuedAt ?? botRun?.startedAt)}
            </Typography>
          </Stack>

          {/* Error operacional concreto registrado en la edición. Cubre las fallas
              de encadenamiento (surfaceChainFailure), que no dejan WorkerEvent ni
              BotRun y por eso no aparecen en el bloque de Diagnóstico de abajo. */}
          {(dndaError || sadaicError) && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              {dndaError && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  <strong>DNDA:</strong> {dndaError}
                </Alert>
              )}
              {sadaicError && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  <strong>SADAIC:</strong> {sadaicError}
                </Alert>
              )}
            </Stack>
          )}

          {/* Diagnóstico */}
          {(deadLetter || botRun) && (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                {deadLetter && <StatusChip status={deadLetter.status} />}
                {botRun && <StatusChip status={botRun.status} />}
                {deadLetter && (
                  <RetryIndicator retryCount={deadLetter.retryCount} manualRetryCount={deadLetter.manualRetryCount} />
                )}
                {deadLetter && (
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }} color="text.secondary">
                    {deadLetter.jobType}
                  </Typography>
                )}
                {botRun?.currentPhase && (
                  <Typography variant="body2" color="text.secondary">
                    fase: {botRun.currentPhase}
                  </Typography>
                )}
              </Stack>
              {errText(deadLetter?.error) && (
                <Alert severity="error" sx={{ py: 0.5 }}>
                  {errText(deadLetter?.error)}
                </Alert>
              )}
              {botRun?.interventionReason && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {botRun.interventionReason}
                </Alert>
              )}
              {deadLetter?.payload && (
                <Accordion variant="outlined" disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" color="text.secondary">
                      Datos del trámite que falló (referencia para elegir qué corregir)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box component="pre" sx={{ m: 0, fontSize: 11, maxHeight: 320, overflow: "auto" }}>
                      {JSON.stringify(deadLetter.payload?.payload ?? deadLetter.payload, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}

          {/* Guía de resolución */}
          {guide.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="h4" sx={{ mb: 0.5 }}>
                Causas típicas y cómo resolver
              </Typography>
              <Stack spacing={0.75}>
                {guide.map((g, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      • {g.cause}
                    </Typography>
                    {guideAction(g.action)}
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* Editor de correcciones */}
          {showOverrides && (
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1.5 }} />
              <OverridesEditor store={store} editionId={edition.id} />
            </Box>
          )}

          {/* Acciones */}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {/* Los dos tipos de recuperación se veían idénticos, y no lo son:
                unas vuelven a correr el bot y otras solo mueven el estado.
                Apretar la segunda esperando la primera es lo que dejó una
                edición seis días quieta con el trámite venciéndose. */}
            {recoveries.map((opt) => (
              <Tooltip
                key={opt.mutation}
                title={
                  opt.relaunches
                    ? "Vuelve a correr el bot."
                    : "Solo completa el avance de estado: NO vuelve a correr el bot."
                }
              >
                <span>
                  <Button
                    variant={opt.relaunches ? "contained" : "outlined"}
                    startIcon={opt.relaunches ? <BuildIcon /> : <FastForwardIcon />}
                    disabled={store.resolving === edition.id}
                    onClick={() => confirmAndResolve(opt.mutation, opt.label)}
                  >
                    {store.resolving === edition.id ? "…" : opt.label}
                  </Button>
                </span>
              </Tooltip>
            ))}
            {!showOverrides && (
              <Button variant="outlined" startIcon={<HealingIcon />} onClick={() => setShowOverrides(true)}>
                Correcciones del trámite
              </Button>
            )}
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => router.goTo(`/signedEdition/view/${edition.id}`)}>
              Ver edición
            </Button>
            <Button variant="outlined" startIcon={<ScienceIcon />} onClick={() => router.goTo(`/simulation?editionId=${edition.id}`)}>
              Ver en simulación
            </Button>
            {deadLetter && (
              <Tooltip title="Reintentar el job puntual (modificando campos del job si la política lo permite)">
                <Button variant="outlined" onClick={() => store.openRetry(deadLetter)}>
                  Destrabar job
                </Button>
              </Tooltip>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }
);
